/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  FileText, PenLine, Stamp, Users, Heart, Gift, Scale, Calendar,
} from 'lucide-react';
import GuidePage from '@/components/library/GuidePage';

const config = {
  path: '/testament',
  slug: 'testament',
  breadcrumbLabel: 'Testament',
  readingTime: '7 min',
  lastUpdated: '18 février 2026',
  seoTitle: 'Testament : olographe, authentique, conseils | HÉRITA',
  seoDescription:
    "Comprenez facilement un testament. Découvrez ses formes (olographe, authentique), les volontés exprimées, la réserve héréditaire et analysez votre document avec HÉRITA.",

  hero: {
    eyebrow: 'Guides HÉRITA',
    title: 'Comprendre un testament',
    subtitle:
      "Le testament est l'acte par lequel une personne exprime ses volontés pour après son décès. HÉRITA vous aide à comprendre ce que le défunt a souhaité, qui sont les bénéficiaires et quelles sont les conséquences pour les héritiers.",
  },

  definition: {
    eyebrow: 'Définition',
    title: "Qu'est-ce qu'un testament\u00A0?",
    paragraphs: [
      <>
        Un testament est l'acte par lequel une personne — le <em>testateur</em>
        {' '}— exprime, par écrit, ce qu'elle souhaite voir advenir de ses biens
        après sa mort. C'est un document profondément personnel,{' '}
        <strong className="font-semibold text-[#1A2530]">révocable à tout moment</strong>,
        qui ne produit ses effets qu'au décès du rédacteur.
      </>,
      <>
        Il existe principalement deux formes&nbsp;: le <em>testament olographe</em>,
        rédigé de la main même du testateur, daté et signé, et le{' '}
        <em>testament authentique</em>, dicté par le testateur à deux notaires
        (ou un notaire et deux témoins). Le premier est gratuit et libre&nbsp;;
        le second offre une sécurité juridique maximale et est conservé chez le
        notaire.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Que peut-on prévoir&nbsp;?</strong>{' '}
        Désigner des légataires (bénéficiaires d'un legs), répartir précisément
        ses biens, exhéréder un héritier non réservataire, désigner un
        exécuteur testamentaire, exprimer des volontés funéraires, ou encore
        favoriser une œuvre, un ami, un conjoint pacsé.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Une limite essentielle&nbsp;: la réserve héréditaire.</strong>{' '}
        En France, le testateur ne peut pas déshériter ses enfants. Une part
        minimale du patrimoine — la <em>réserve héréditaire</em> — leur est
        légalement garantie. Seule la part restante, dite <em>quotité disponible</em>,
        peut être attribuée librement.
      </>,
      <>
        À l'ouverture de la succession, le notaire interroge le Fichier
        central des dispositions de dernières volontés pour vérifier qu'aucun
        testament authentique n'a été déposé. Si un testament est retrouvé, il
        est ouvert, lu aux héritiers et exécuté selon les volontés du défunt,
        dans le respect de la réserve.
      </>,
      <>
        Comprendre un testament suppose donc d'identifier ses bénéficiaires,
        de mesurer ce qui leur est attribué et de vérifier que la quotité
        disponible n'est pas dépassée. C'est exactement ce que HÉRITA fait
        pour vous, ainsi que le lien avec l'éventuel{' '}
        <Link to="/acte-notoriete" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">acte de notoriété</Link>
        {' '}qui suivra.
      </>,
    ],
  },

  usage: {
    eyebrow: 'Utilité',
    title: "À quoi sert-il\u00A0?",
    items: [
      { title: 'Exprimer ses volontés', description: "Le testament permet de décider à qui ira tel bien, à quelle œuvre on souhaite léguer une somme, ou comment seront organisées ses obsèques." },
      { title: 'Avantager certaines personnes', description: "Conjoint non marié, ami proche, association : un testament permet de favoriser ceux que la loi n'aurait pas spontanément protégés." },
      { title: 'Désigner un exécuteur', description: "Le testateur peut nommer une personne chargée de veiller au respect de ses volontés et d'organiser leur exécution après son décès." },
      { title: 'Protéger un proche fragile', description: "Le testament permet d'organiser la protection d'un enfant handicapé, d'un conjoint vulnérable ou d'un héritier mineur." },
    ],
  },

  content: {
    eyebrow: 'Contenu',
    title: "Que contient un testament\u00A0?",
    intro: "Sept éléments structurent un testament. Leur lecture permet de comprendre la volonté du défunt et ses implications pour chaque héritier.",
    cards: [
      { Icon: PenLine, title: 'Identité du testateur', description: "Nom, prénom, date et lieu de naissance, adresse, et capacité juridique de la personne qui dispose." },
      { Icon: Calendar, title: 'Date de rédaction', description: "La date précise du testament est essentielle : seul le dernier testament en date est pris en compte." },
      { Icon: Users, title: 'Bénéficiaires', description: "Les personnes nommément désignées comme légataires, avec leur lien éventuel avec le testateur." },
      { Icon: Gift, title: 'Legs', description: "Description précise des biens ou sommes légués : legs universel, à titre universel, ou particulier." },
      { Icon: Scale, title: 'Réserve et quotité', description: "Le respect des parts réservées aux héritiers réservataires (enfants) et l'utilisation de la quotité disponible." },
      { Icon: Heart, title: 'Volontés personnelles', description: "Souhaits funéraires, soins à apporter, transmission d'un message ou d'objets symboliques aux proches." },
      { Icon: Stamp, title: 'Signature et lieu', description: "Le testament olographe doit être entièrement manuscrit, daté et signé. Tout défaut peut entraîner sa nullité." },
      { Icon: FileText, title: 'Exécuteur testamentaire', description: "Personne désignée pour faire respecter les dispositions du testament et accompagner les héritiers." },
    ],
  },

  checks: {
    eyebrow: 'Vigilance',
    title: 'Les points importants à vérifier',
    intro: "Un testament peut être contesté pour défaut de forme. Quelques vérifications simples permettent d'éviter les surprises.",
    items: [
      "Confirmer que le testament olographe est intégralement de la main du testateur, daté et signé.",
      "Vérifier qu'il s'agit bien du dernier testament en date — tout testament postérieur annule les précédents.",
      "S'assurer que la réserve héréditaire des enfants est respectée. Sinon, ils peuvent demander une réduction.",
      "Identifier précisément chaque legs : la personne, le bien, le montant exact.",
      "Repérer toute clause inhabituelle : interdiction d'aliéner, condition suspensive, charge particulière.",
      "Vérifier la mention d'un exécuteur testamentaire et ses pouvoirs.",
    ],
  },

  autoChecks: {
    eyebrow: 'Sans rien demander',
    title: 'Ce que HÉRITA identifie automatiquement',
    intro: "Dès l'import, HÉRITA repère les bénéficiaires, les legs et les points sensibles du document.",
    items: [
      'Testateur identifié et capacité juridique',
      'Forme du testament (olographe, authentique)',
      'Date et lieu de rédaction',
      'Bénéficiaires nommés et leur lien éventuel',
      'Legs détaillés (universel, à titre universel, particulier)',
      'Respect de la réserve héréditaire',
      'Volontés personnelles et clauses particulières',
      'Questions à poser à votre notaire',
    ],
  },

  example: {
    eyebrow: 'Avant · Après',
    title: 'Un exemple simplifié',
    intro: "À gauche, un extrait fictif de testament olographe. À droite, ce qu'HÉRITA en aurait expliqué.",
    extract: (
      <>
        «&nbsp;Ceci est mon testament. Je soussignée Madame Marie LEFEBVRE,
        née le 12 juin 1948 à Lyon, déclare léguer&nbsp;: à ma fille Sophie,
        ma maison de famille sise [Adresse], avec son mobilier&nbsp;; à mon
        petit-fils Hugo, la somme de TRENTE MILLE EUROS (30 000 €)&nbsp;; à
        l'association «&nbsp;Les Enfants du Cœur&nbsp;», la somme de DIX MILLE
        EUROS (10 000 €). Je désigne mon notaire, Maître Claire MORIN, comme
        exécuteur testamentaire. Fait à Lyon, le 4 octobre 2023.&nbsp;»
      </>
    ),
    headline: "Ce que cela signifie pour Sophie et les autres",
    bullets: [
      { strong: 'Sophie reçoit', rest: ' la maison de famille en legs particulier, avec son mobilier.' },
      { strong: 'Hugo, petit-fils', rest: ', reçoit un legs en numéraire de 30 000 €.' },
      { strong: 'L\'association', rest: ' bénéficie d\'un legs caritatif de 10 000 €, exonéré de droits.' },
      { strong: 'Maître Claire MORIN', rest: ' est désignée comme exécutrice testamentaire et veillera à l\'exécution des volontés.' },
      { strong: 'La quotité disponible', rest: ' devra être vérifiée : si Sophie est l\'unique enfant, la réserve héréditaire représente la moitié du patrimoine.' },
    ],
  },

  faq: [
    { q: "Qu'est-ce qu'un testament ?", a: "C'est un acte écrit par lequel une personne exprime ses volontés sur le sort de ses biens après sa mort. Il est strictement personnel et peut être modifié ou révoqué à tout moment du vivant du testateur." },
    { q: "Quelles sont les formes valables ?", a: "Trois formes principales : olographe (manuscrit, daté, signé), authentique (dicté à un notaire en présence de témoins) et mystique (forme rare où le testament est remis cacheté au notaire). L'olographe et l'authentique sont les plus utilisés." },
    { q: "Peut-on déshériter ses enfants ?", a: "Non. Le droit français impose une réserve héréditaire qui revient obligatoirement aux enfants. Seule la quotité disponible — variable selon le nombre d'enfants — peut être léguée à des tiers." },
    { q: "Combien coûte un testament authentique ?", a: "Le tarif est encadré : comptez environ 115 € HT d'émoluments pour l'acte, auxquels s'ajoutent les recherches et les frais d'enregistrement au Fichier central des dispositions de dernières volontés." },
    { q: "Comment révoquer un testament ?", a: "Trois options : rédiger un nouveau testament qui annule expressément le précédent, détruire matériellement le testament olographe, ou faire une déclaration de révocation devant notaire." },
    { q: "Comment HÉRITA l'analyse-t-il ?", a: "HÉRITA lit le testament, identifie le testateur, sa date, les bénéficiaires nommés, chaque legs avec sa nature et son montant, et vérifie en français clair si la réserve héréditaire est respectée. Vous obtenez une lecture personnalisée en quelques minutes." },
    { q: "Que faire si je trouve un testament après le décès ?", a: "Apportez-le à un notaire. Il vérifiera sa validité formelle, le déposera officiellement, et l'intégrera au règlement de la succession. Ne le détruisez en aucun cas, même si son contenu vous semble défavorable." },
    { q: "Peut-on contester un testament ?", a: "Oui, dans certains cas : insanité d'esprit du testateur, vice de forme (testament olographe non manuscrit), violation de la réserve héréditaire, ou captation d'héritage. L'action en nullité doit être engagée dans les 5 ans suivant le décès." },
    { q: "Un testament conjoint est-il possible ?", a: "Non. Le droit français interdit le testament conjoint, c'est-à-dire un testament unique fait par deux personnes ensemble. Chaque personne doit rédiger son propre testament, même si les couples souhaitent souvent s'organiser ensemble." },
    { q: "Quel rapport avec la succession ?", a: "Le testament s'applique automatiquement à l'ouverture de la succession. Il est lu, exécuté par le notaire et intégré à la déclaration de succession. Il modifie la dévolution légale dans les limites posées par la loi (réserve, quotité disponible)." },
  ],

  finalCta: {
    title: "Vous avez retrouvé un testament\u00A0?",
    subtitle: "Déposez le document. HÉRITA identifie automatiquement le testateur, les bénéficiaires, chaque legs, le respect de la réserve héréditaire et les démarches à entreprendre pour vous fournir une lecture claire et personnalisée.",
  },
};

const Testament = () => <GuidePage config={config} />;
export default Testament;
